/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect } from 'react';
import useMounted from '../../../shared/useMount/useMount';
import {
  formattedDate,
  returnFormattedDate
} from '../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import IEnergyManagementProps, {
  IAny,
  IRequestContext
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import CarbonScopeOneDetailsComponent from '../../CarbonSiteViewDetailsComponent/CarbonScopeOneDetailWidget/CarbonScopeOneDetailWidget';
import { UtilityType } from '../../../shared/Utils/Constants';
import FilterScope1Action from '../../../../context/FilterScope1Context/FilterScope1Actions';
import PortfolioCarbonScopeOneSavingsBySiteComponent from '../PortfolioCarbonScopeOneSavingsBySiteWidget/PortfolioCarbonScopeOneSavingsBySiteComponent';
import { getCarbonVDEndDateBasedOnSub } from '../../../shared/Utils/CarbonViewDetailsUtils';
import Iconsscope1Filter from '../../../../images/Iconsscope1Filter.svg';
import './PortfolioCarbonScopeOneDetailsComponent.scss';
// import FilterModal from './FilterModal';
import { useFilterScope1Context } from '../../../../context/FilterScope1Context/FilterScope1Context';
import { ApiContext } from '../../../../context/ApiContext';
import {
  EmaasSubscriptionType,
  SmartPowerSubscriptionType
} from '../../../shared/Enums';
import CarbonScopeOneDetailWidgetSeparateAPI from '../../CarbonSiteViewDetailsComponent/CarbonScopeOneDetailWidget/CarbonScopeOneDetailWidgetSeparateAPI';
const getAdjustedEndDate = (userEndDate: string | Date) => {
  // Parse the user's end date
  const userDate = new Date(userEndDate);
  const currentDate = new Date();

  // Check if the user's end date month and year are the same as the current month and year
  if (
    userDate.getMonth() === currentDate.getMonth() &&
    userDate.getFullYear() === currentDate.getFullYear()
  ) {
    // Subtract one month from the user's end date
    userDate.setMonth(userDate.getMonth() - 1);
  }

  return userDate.toISOString();
};

export const getConnectorConfigCarbon = (
  toggleFeatureScopeOne: boolean | undefined,
  requestContext: IRequestContext,
  isSitePremium: IAny
) => {
  const dataSource = requestContext.context.dataSource || '';
  const params = {
    startDate: requestContext.context.startTime
      ? formattedDate(requestContext.context.startTime)
      : '',
    endDate: getCarbonVDEndDateBasedOnSub(
      requestContext.context.subscriptionType!,
      requestContext.context.endTime!,
      requestContext.context.startTime!,
      dataSource
    )
  };
  if (toggleFeatureScopeOne) {
    return {
      name: isSitePremium
        ? 'portfolio-carbon-site-summary'
        : 'portfolio-carbon-new',
      options: {
        ...params,
        scopeType: 'scope1',
        dataSource: isSitePremium ? requestContext.context.dataSource || '' : ''
      }
    };
  } else {
    // return {
    //   name: 'portfolio-utility-gas',
    //   options: {
    //     ...params,
    //     allDetails: 'true',
    //     utilityType: 'gas'
    //   }
    // };

    return {
      name: isSitePremium
        ? 'portfolio-carbon-site-summary'
        : 'portfolio-carbon-new-v1',
      options: {
        ...params,
        allDetails: 'true',
        utilityType: 'gas',
        dataSource: isSitePremium ? requestContext.context.dataSource || '' : ''
      }
    };
  }
};

const PortfolioCarbonScopeOneDetailsComponent = (
  props: IEnergyManagementProps
) => {
  const { bearerToken, requestContext } = props;
  const isMounted = useMounted();
  const emaasContext = useContext(ApiContext);
  const { toggleFeatureScopeOne, toggleSeparateApi } = emaasContext!;
  const dataSource = requestContext.context.dataSource || '';

  const isSitePremium =
    requestContext.context.subscriptionType ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION && toggleSeparateApi;
  const getConnectorConfigGraph = (
    toggleFeatureScopeOne: boolean | undefined,
    requestContext: IRequestContext
  ) => {
    const dataSource = requestContext.context.dataSource || '';
    const params = {
      startDate: requestContext.context.startTime
        ? formattedDate(requestContext.context.startTime)
        : '',
      endDate: getCarbonVDEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!,
        dataSource
      )
    };
    if (toggleFeatureScopeOne) {
      return {
        name: 'portfolio-carbon-new-graph',
        options: {
          ...params,
          scopeType: 'scope1',
          dataSource
        }
      };
    } else {
      return {
        name: 'portfolio-carbon-site-graph',
        options: {
          ...params,
          allDetails: 'true',
          dataSource
        }
      };
    }
  };

  const getConnectorConfigTable = (
    toggleFeatureScopeOne: boolean | undefined,
    requestContext: IRequestContext
  ) => {
    const dataSource = requestContext.context.dataSource || '';
    const params = {
      startDate: requestContext.context.startTime
        ? formattedDate(requestContext.context.startTime)
        : '',
      endDate: getCarbonVDEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!,
        dataSource
      )
    };
    if (toggleFeatureScopeOne) {
      return {
        name: 'portfolio-carbon-new-table',
        options: {
          ...params,
          scopeType: 'scope1',
          groupType: 'scopetype',
          dataSource
        }
      };
    } else {
      return {
        name: 'portfolio-carbon-site-table',
        options: {
          ...params,
          allDetails: 'true',
          scopeType: 'scope1',
          dataSource
        }
      };
    }
  };

  const connectorConfig = getConnectorConfigCarbon(
    toggleFeatureScopeOne,
    requestContext,
    isSitePremium
  );
  const connectorConfigForGraph = getConnectorConfigGraph(
    toggleFeatureScopeOne,
    requestContext
  );
  const connectorConfigForTable = getConnectorConfigTable(
    toggleFeatureScopeOne,
    requestContext
  );

  useEffect(() => {
    if (isMounted) {
      dataConnector && dataConnector.actions && dataConnector.actions.getAll();
      if (isSitePremium) {
        dataConnectorForGraph?.actions?.getAll();
        dataConnectorForTable?.actions?.getAll();
      }
    }
  }, [requestContext, dataSource]);

  const [, dispatch] = useFilterScope1Context();
  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });
  const [dataConnectorForGraph] = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigForGraph,
      requestContext,
      bearerToken
    },
    isSitePremium
  );

  const [dataConnectorForTable] = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigForTable,
      requestContext,
      bearerToken
    },
    isSitePremium
  );

  const showFilterModalPopup = () =>
    dispatch(FilterScope1Action.showFilterModal());
  return (
    <div className="total-emissions-scope-one-details">
      {/* {toggleFeatureScopeOne && (
        <span
          className="filter_scope1-right-content"
          onClick={showFilterModalPopup}
        >
          <img src={Iconsscope1Filter} />
        </span>
      )}
      <FilterModal dataConnector={dataConnector} /> */}
      {isSitePremium ? (
        <CarbonScopeOneDetailWidgetSeparateAPI
          dataConnector={dataConnector}
          dataConnectorForGraph={dataConnectorForGraph}
          dataConnectorForTable={dataConnectorForTable}
          requestContext={requestContext}
          dataSourceType={props.dataSourceType ? props.dataSourceType : ''}
        />
      ) : (
        <CarbonScopeOneDetailsComponent dataConnector={dataConnector} />
      )}

      <PortfolioCarbonScopeOneSavingsBySiteComponent
        dataConnector={isSitePremium ? dataConnectorForTable : dataConnector}
        UtilityBillType={UtilityType.Gas}
      />
    </div>
  );
};
export default PortfolioCarbonScopeOneDetailsComponent;
